import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared";
import { ContactModel } from "../ContactModel"
import { useMatching, useNeed, useProperty } from "@/OneLiners";
import { useDocumentEnricher } from "@/Domains/Document";


/**
 * =====================================================
 * Hook: useContactEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich Contact domain data with all relevant relations.
 * Combines users, tasks, media files, and audit info into the ContactModel.
 *
 * Responsibilities:
 * - Fetch and maintain related domain data (users, tasks, media files)
 * - Perform audit enrichment in O(1) using precomputed user Map
 * - Track combined async status and errors
 * - Provide helper functions to enrich a single contact, by ID, or a full map
 *
 * Behavior:
 * - Resolves agent users efficiently
 * - Attaches audit fields (createdBy, updatedBy, etc.) using the shared audit hook
 * - Returns enriched data without unnecessary re-computation
 *
 * Notes:
 * - Compatible with both list and detail views of Contacts
 * - Designed for performance at scale with large agent/target datasets
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export function useContactEnricher() {

    /**
         * Centralized audit enrichment hook
         * - Provides O(1) user resolution via precomputed Map
         * - Exposes audit enrichment helpers and status/error
         */
    const {
        enricherResponse: auditEnricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher<ContactModel>();

    /**
     * Needs data – used to link contacts with their needs
     */
    const {
        dataMap: needMap,
        stateResponse: needStateResponse,
    } = useNeed();

    /**
     * Properties data – used to enrich matchings
     */
    const {
        dataMap: propertyMap,
        stateResponse: propertyStateResponse,
    } = useProperty();

    /**
     * Matchings data – used to link contacts through needs to properties
     */
    const {
        dataMap: matchingMap,
        stateResponse: matchingStateResponse,
    } = useMatching();

    /**
     * Document domain operations
     * Used to enrich mandates with associated documents
     */
    const {
        enricherResponse: documentEnricherResponse,
        findByResource
    } = useDocumentEnricher();

    /**
     * Combined response – aggregates status and errors from all dependencies
     */
    const enricherResponse: ActionResponse = {
        status: ActionStatusHelper.combine(
            auditEnricherResponse.status,
            needStateResponse.status,
            propertyStateResponse.status,
            matchingStateResponse.status,
            documentEnricherResponse.status
        ),
        error: auditEnricherResponse.error ??
            needStateResponse.error ??
            matchingStateResponse.error ??
            propertyStateResponse.error ??
            documentEnricherResponse.error
    };

    /**
     * Enriches a single Contact entity
     * - Adds audit data (createdBy, updatedBy, etc.)
     * - Resolves agent from user list
     * - Attaches related needs and matchings
     */
    const getEnrichedContact = (contact?: ContactModel): ContactModel | undefined => {
        return contact ? getStrictEnrichedContact(contact) : undefined;
    }

    const getStrictEnrichedContact = (contact: ContactModel): ContactModel => {

        const filteredNeedMap = needMap.filter(need => need.contactId === contact.id);
        
        const filteredMatchingMap = matchingMap
            .filter(matching => !!filteredNeedMap.get(matching.needId))
            .map(matching => {
                return {
                    ...matching,
                    property: propertyMap.get(matching.propertyId)
                }
            });
        
        return {
            ...getEnrichedAudit(contact) as ContactModel,
            agent: getUserById(contact.agentId),
            needList: filteredNeedMap.toArray(),
            matchingList: filteredMatchingMap.toArray(),
        }
    }

    /** =====================================================
         * Enrich an entire map of activities
         * 
         * @param contactMap - Map keyed by contact ID containing ContactModel or undefined
         * @returns A new Map with the same keys, each ContactModel enriched with:
         *          - initiatorUser
         *          - assignedAgent
         *          - task
         *          - enriched contactMediaFiles
         *          - audit info (createdByUser, updatedByUser, etc.)
         * 
         * Notes:
         * - Preserves keys even if the value is undefined
         * - Uses O(1) lookups via Maps for users, tasks, media files
         * ===================================================== */
    const getEnrichedContactMap = (contactMap: RecordMap<ContactModel>): RecordMap<ContactModel> => {

        return contactMap.map<ContactModel>(getStrictEnrichedContact);
    }

    /**
    * Public API – exposed to consumers of the hook
    */
    return {
        enricherResponse,
        userMap,
        needMap,
        propertyMap,
        matchingMap,
        getEnrichedContact,
        getStrictEnrichedContact,
        getEnrichedContactMap,
        findByDocumentResource : findByResource,
    }
}